/**
 * Phase 1b — Memory-optimization PR foundation: HATCH boundary binary
 * encoder/decoder.
 *
 * HATCH `boundaryPaths[].edges[]` is the single largest per-entity payload
 * on real-world fixtures (АБИ1.dxf carries 100+ paths with O(10²) edges
 * each — JS object headers dominate). This module packs the edge data into
 * typed-array form so a compact store can keep the same boundary topology
 * in ~10% of the memory footprint.
 *
 * Schema (Opt 6 in the plan):
 *   - `pathFlags[i]` — `pathTypeFlag` for path `i`
 *   - `pathEdgeOffsets[i]` — cumulative edge index where path `i` begins;
 *     `pathEdgeOffsets[i+1] - pathEdgeOffsets[i]` = edge count of path `i`.
 *     Length = `paths.length + 1` (final entry is the total edge count).
 *   - `edgeTypes[k]` — type tag for edge `k` (`0 = line`, `1 = arc`,
 *     `2 = ellipticArc`).
 *   - `{line,arc,ellipticArc}Offsets[k]` — index into the matching
 *     `*Params` array for edge `k`. Set only for the matching type;
 *     other-type slots carry `0` (callers MUST dispatch on `edgeTypes[k]`
 *     before reading).
 *   - `lineParams`     — 4 floats per line edge: `start.x, start.y, end.x, end.y`.
 *   - `arcParams`      — 6 floats per arc edge: `center.x, center.y, radius,
 *                        startAngle, endAngle, counterclockwise ? 1 : 0`.
 *   - `ellipticArcParams` — 8 floats per ellipticArc edge:
 *                           `center.x, center.y, majorAxisEnd.x, majorAxisEnd.y,
 *                           minorRatio, startAngle, endAngle,
 *                           counterclockwise ? 1 : 0`.
 *
 * Field-name verification:
 *   Edge variants per `hatchBoundaryEdgeSchema` (model.ts:487-510):
 *     line          → { edgeType: "line", start, end }
 *     arc           → { edgeType: "arc", center, radius, startAngle,
 *                       endAngle, counterclockwise }
 *     ellipticArc   → { edgeType: "ellipticArc", center, majorAxisEnd,
 *                       minorRatio, startAngle, endAngle, counterclockwise }
 *
 *   NOTE: the field is `majorAxisEnd` (not `major`) and `minorRatio`
 *   (not `ratio`) — both correctly reflected here, deviating from the
 *   informal field names in the Phase 1b spec.
 *
 * Empty-paths note:
 *   `hatchEntitySchema` accepts `boundaryPaths.length === 0` (degenerate
 *   round-trip — see model.ts:527). The encoder writes
 *   `pathEdgeOffsets = [0]` and all params arrays empty in that case.
 */

import type {
  HatchBoundaryEdge,
  HatchBoundaryPath,
} from "@dxf-ai-editor/drawing-model";

export const HATCH_EDGE_TYPE_LINE = 0;
export const HATCH_EDGE_TYPE_ARC = 1;
export const HATCH_EDGE_TYPE_ELLIPTIC_ARC = 2;

const LINE_FLOATS_PER_EDGE = 4;
const ARC_FLOATS_PER_EDGE = 6;
const ELLIPTIC_ARC_FLOATS_PER_EDGE = 8;

export interface HatchBoundaryBinary {
  pathFlags: Uint8Array;
  /** Length `paths.length + 1`; last entry is the total edge count. */
  pathEdgeOffsets: Uint32Array;
  edgeTypes: Uint8Array;
  lineParams: Float32Array;
  arcParams: Float32Array;
  ellipticArcParams: Float32Array;
  /** For edge `k`: if `edgeTypes[k] === LINE`, index into `lineParams / 4`. */
  lineOffsets: Uint32Array;
  /** For edge `k`: if `edgeTypes[k] === ARC`, index into `arcParams / 6`. */
  arcOffsets: Uint32Array;
  /** For edge `k`: if `edgeTypes[k] === ELLIPTIC_ARC`, index into params/8. */
  ellipticArcOffsets: Uint32Array;
}

/* ── ENCODER ────────────────────────────────────────────────────────── */

export function encodeBoundaryBinary(
  paths: readonly HatchBoundaryPath[],
): HatchBoundaryBinary {
  // First pass — count edges per type so we can size the typed arrays
  // exactly. Avoids the cost of repeated grow/copy on long boundaries.
  let totalEdges = 0;
  let lineCount = 0;
  let arcCount = 0;
  let ellipticArcCount = 0;
  for (const path of paths) {
    for (const edge of path.edges) {
      totalEdges += 1;
      switch (edge.edgeType) {
        case "line":
          lineCount += 1;
          break;
        case "arc":
          arcCount += 1;
          break;
        case "ellipticArc":
          ellipticArcCount += 1;
          break;
      }
    }
  }

  const pathFlags = new Uint8Array(paths.length);
  const pathEdgeOffsets = new Uint32Array(paths.length + 1);
  const edgeTypes = new Uint8Array(totalEdges);
  const lineOffsets = new Uint32Array(totalEdges);
  const arcOffsets = new Uint32Array(totalEdges);
  const ellipticArcOffsets = new Uint32Array(totalEdges);
  const lineParams = new Float32Array(lineCount * LINE_FLOATS_PER_EDGE);
  const arcParams = new Float32Array(arcCount * ARC_FLOATS_PER_EDGE);
  const ellipticArcParams = new Float32Array(
    ellipticArcCount * ELLIPTIC_ARC_FLOATS_PER_EDGE,
  );

  let edgeIdx = 0;
  let lineIdx = 0;
  let arcIdx = 0;
  let ellipticArcIdx = 0;
  let cumulativeEdges = 0;

  pathEdgeOffsets[0] = 0;
  for (let p = 0; p < paths.length; p += 1) {
    const path = paths[p];
    pathFlags[p] = path.pathTypeFlag & 0xff;
    for (const edge of path.edges) {
      switch (edge.edgeType) {
        case "line": {
          edgeTypes[edgeIdx] = HATCH_EDGE_TYPE_LINE;
          lineOffsets[edgeIdx] = lineIdx;
          const base = lineIdx * LINE_FLOATS_PER_EDGE;
          lineParams[base + 0] = edge.start.x;
          lineParams[base + 1] = edge.start.y;
          lineParams[base + 2] = edge.end.x;
          lineParams[base + 3] = edge.end.y;
          lineIdx += 1;
          break;
        }
        case "arc": {
          edgeTypes[edgeIdx] = HATCH_EDGE_TYPE_ARC;
          arcOffsets[edgeIdx] = arcIdx;
          const base = arcIdx * ARC_FLOATS_PER_EDGE;
          arcParams[base + 0] = edge.center.x;
          arcParams[base + 1] = edge.center.y;
          arcParams[base + 2] = edge.radius;
          arcParams[base + 3] = edge.startAngle;
          arcParams[base + 4] = edge.endAngle;
          arcParams[base + 5] = edge.counterclockwise ? 1 : 0;
          arcIdx += 1;
          break;
        }
        case "ellipticArc": {
          edgeTypes[edgeIdx] = HATCH_EDGE_TYPE_ELLIPTIC_ARC;
          ellipticArcOffsets[edgeIdx] = ellipticArcIdx;
          const base = ellipticArcIdx * ELLIPTIC_ARC_FLOATS_PER_EDGE;
          ellipticArcParams[base + 0] = edge.center.x;
          ellipticArcParams[base + 1] = edge.center.y;
          ellipticArcParams[base + 2] = edge.majorAxisEnd.x;
          ellipticArcParams[base + 3] = edge.majorAxisEnd.y;
          ellipticArcParams[base + 4] = edge.minorRatio;
          ellipticArcParams[base + 5] = edge.startAngle;
          ellipticArcParams[base + 6] = edge.endAngle;
          ellipticArcParams[base + 7] = edge.counterclockwise ? 1 : 0;
          ellipticArcIdx += 1;
          break;
        }
      }
      edgeIdx += 1;
    }
    cumulativeEdges += path.edges.length;
    pathEdgeOffsets[p + 1] = cumulativeEdges;
  }

  return {
    pathFlags,
    pathEdgeOffsets,
    edgeTypes,
    lineParams,
    arcParams,
    ellipticArcParams,
    lineOffsets,
    arcOffsets,
    ellipticArcOffsets,
  };
}

/* ── DECODER ────────────────────────────────────────────────────────── */

export function decodeBoundaryBinary(
  bin: HatchBoundaryBinary,
): HatchBoundaryPath[] {
  const pathCount = bin.pathFlags.length;
  const result: HatchBoundaryPath[] = new Array(pathCount);

  for (let p = 0; p < pathCount; p += 1) {
    const startEdge = bin.pathEdgeOffsets[p];
    const endEdge = bin.pathEdgeOffsets[p + 1];
    const edges: HatchBoundaryEdge[] = new Array(endEdge - startEdge);
    for (let k = startEdge, j = 0; k < endEdge; k += 1, j += 1) {
      const t = bin.edgeTypes[k];
      if (t === HATCH_EDGE_TYPE_LINE) {
        const base = bin.lineOffsets[k] * LINE_FLOATS_PER_EDGE;
        edges[j] = {
          edgeType: "line",
          start: {
            x: bin.lineParams[base + 0],
            y: bin.lineParams[base + 1],
          },
          end: {
            x: bin.lineParams[base + 2],
            y: bin.lineParams[base + 3],
          },
        };
      } else if (t === HATCH_EDGE_TYPE_ARC) {
        const base = bin.arcOffsets[k] * ARC_FLOATS_PER_EDGE;
        edges[j] = {
          edgeType: "arc",
          center: {
            x: bin.arcParams[base + 0],
            y: bin.arcParams[base + 1],
          },
          radius: bin.arcParams[base + 2],
          startAngle: bin.arcParams[base + 3],
          endAngle: bin.arcParams[base + 4],
          counterclockwise: bin.arcParams[base + 5] !== 0,
        };
      } else if (t === HATCH_EDGE_TYPE_ELLIPTIC_ARC) {
        const base =
          bin.ellipticArcOffsets[k] * ELLIPTIC_ARC_FLOATS_PER_EDGE;
        edges[j] = {
          edgeType: "ellipticArc",
          center: {
            x: bin.ellipticArcParams[base + 0],
            y: bin.ellipticArcParams[base + 1],
          },
          majorAxisEnd: {
            x: bin.ellipticArcParams[base + 2],
            y: bin.ellipticArcParams[base + 3],
          },
          minorRatio: bin.ellipticArcParams[base + 4],
          startAngle: bin.ellipticArcParams[base + 5],
          endAngle: bin.ellipticArcParams[base + 6],
          counterclockwise: bin.ellipticArcParams[base + 7] !== 0,
        };
      } else {
        // Defensive: unknown type tag. Surface as a degenerate line at
        // origin rather than silently dropping — caller should validate
        // the typed-array round-trip in unit tests.
        throw new Error(
          `compactHatchBoundaryBinary.decodeBoundaryBinary: unknown edge type tag ${t} at edge ${k}`,
        );
      }
    }
    result[p] = {
      pathTypeFlag: bin.pathFlags[p],
      edges,
    };
  }

  return result;
}
